import type { JSX } from '@builder.io/qwik';
import { useContextProvider } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { AppDataContext } from '~/contexts/AppDataContext';
import { NavContext } from '~/contexts/NavContext';
import { cva } from 'class-variance-authority';
import {
  getCSSColorBg,
  getCSSHeightSmFactor,
  getCSSHeightMdFactor,
  getCSSHeightLgFactor,
} from '~/utilities/getCSS';
import Nav from '~/components/Layout/Header/Desktop/Nav';
import { SI_PRIMARY_NAV_THEME } from '~/constants/SI_PRIMARY_NAV_THEME';
import type { NavThemeComponentsInterface } from '~/types/AppData/Theme/NavThemeComponentsInterface';

const headerClasses = cva(
  'fixed flex items-center space-between w-full top-[88px] lg:top-[78px] h-12 px-4 z-40' +
    ' overflow-x-scroll md:overflow-x-visible scrollbar-none shadow-header' +
    ' [&_nav]:pr-4 [&>div]:justify-start md:[&>div]:justify-center',
  {
    variants: {
      color: getCSSColorBg(),
      heightSm: getCSSHeightSmFactor(),
      heightMd: getCSSHeightMdFactor(),
      heightLg: getCSSHeightLgFactor(),
    },
  }
);

export default component$((): JSX.Element => {
  const {
    payloadToSSR: {
      layout: { thirdHeader },
    },
    themeComponents: { threeRowsHeader },
  } = useContext(AppDataContext);

  if (!thirdHeader?.links?.value.length) return <></>;

  const {
    thirdLineTextColor,
    thirdLineTextColorHover,
    thirdLineBackgroundColor,
  } = threeRowsHeader;

  const NavThemeComponents: Partial<NavThemeComponentsInterface> = {
    LinkFontSizeLarge: { type: 'TYPOGRAPHY', value: 'tiny' },
    LinkFontSizeMedium: { type: 'TYPOGRAPHY', value: 'tiny' },
    LinkFontSizeSmall: { type: 'TYPOGRAPHY', value: 'tiny' },
    LinkColorTypeNormal: { type: 'COLOR', value: thirdLineTextColor.value },
    LinkColorTypeHover: { type: 'COLOR', value: thirdLineTextColorHover.value },
    LinkCurrentColor: { type: 'COLOR', value: thirdLineTextColor.value },
    LinkColorTypeActive: { type: 'COLOR', value: thirdLineTextColor.value },
    LinkActiveBorderBottom: { type: 'COLOR', value: thirdLineTextColor.value },
    LinkHoverBorderBottom: { type: 'COLOR', value: thirdLineTextColor.value },
    HoverOtherLinksColors: { type: 'COLOR', value: thirdLineTextColor.value },
    LinkDropdownIconColor: { type: 'COLOR', value: thirdLineTextColor.value },
  };

  // SI Header is not built with the theming tool, so we need to hardcode these values
  const navTheme = { ...SI_PRIMARY_NAV_THEME, ...NavThemeComponents };

  useContextProvider(NavContext, {
    header: thirdHeader,
    theme: navTheme,
    hasAccessibility: false,
    navInMobile: true,
    canHaveMore: false,
    grow: false,
    hasThirdHeader: true,
    isThirdHeader: true,
    scrollable: true,
  });

  return (
    <div
      class={headerClasses({
        color: thirdLineBackgroundColor.value,
      })}
    >
      <div class='w-full flex justify-center h-full'>
        {thirdHeader.feedName?.value && (
          <>
            <span class='font-group-tiny !font-[600] uppercase self-center'>
              {thirdHeader.feedName.value}
            </span>
            <span class='bg-black h-3 w-px mx-4 self-center'></span>
          </>
        )}
        <Nav />
      </div>
    </div>
  );
});
